<template>
  <div class="ra-proof-cell">
    <span v-if="!stages.length" class="grey--text">
      {{ $t("riskAssessment.proof.noProofers") }}
    </span>
    <template v-else>
      <v-tooltip bottom>
        <template v-slot:activator="{ on }">
          <v-chip
            v-on="on"
            small
            outlined
            class="ra-proof-chip"
            :color="chipColor"
            @click.stop="openVersions"
          >
            {{ chipText }}
            <v-icon x-small right>mdi-history</v-icon>
          </v-chip>
        </template>
        <span>{{ tooltipText }}</span>
      </v-tooltip>
    </template>

    <v-dialog v-model="versionsDialog" max-width="680" scrollable>
      <v-card>
        <v-card-title>
          {{ $t("riskAssessment.proof.versionsTitle") }}
        </v-card-title>
        <v-card-subtitle class="pb-0">{{ item.title }}</v-card-subtitle>
        <v-card-text class="pt-4">
          <div v-if="loadingVersions" class="text-center py-4">
            <v-progress-circular indeterminate color="primary" />
          </div>
          <div v-else-if="!versions.length" class="grey--text">
            {{ $t("riskAssessment.proof.noVersions") }}
          </div>
          <div v-else>
            <div v-for="v in versions" :key="v.version" class="ra-version">
              <div class="d-flex align-center mb-2">
                <div class="subtitle-2">
                  {{ $t("riskAssessment.proof.version") }} {{ v.version }}
                </div>
                <v-chip
                  v-if="v.version === currentVersion"
                  x-small
                  color="primary"
                  class="ml-2"
                >
                  {{ $t("riskAssessment.proof.current") }}
                </v-chip>
                <v-chip
                  v-if="v.status === 'rejected'"
                  x-small
                  color="error"
                  outlined
                  class="ml-2"
                >
                  {{ $t("riskAssessment.proof.rejected") }}
                </v-chip>
                <v-chip
                  v-else-if="v.status === 'approved'"
                  x-small
                  color="success"
                  outlined
                  class="ml-2"
                >
                  {{ $t("riskAssessment.proof.approved") }}
                </v-chip>
                <div class="flex-grow-1" />
                <div class="text-caption grey--text">
                  {{ dateUtil(new Date(v.createdAt), true, "-") }}
                </div>
              </div>

              <div v-if="v.rejection" class="ra-rejection mb-3">
                <div class="text-caption error--text font-weight-medium">
                  {{ $t("riskAssessment.proof.rejectedBy") }}
                  {{ v.rejection.name }} —
                  {{ dateUtil(new Date(v.rejection.rejectedAt), true, "-") }}
                </div>
                <div class="text-body-2 mt-1">{{ v.rejection.reason }}</div>
              </div>

              <div
                v-for="(stage, si) in v.stages || []"
                :key="stage.order"
                class="d-flex align-center ra-version-row"
              >
                <v-icon
                  x-small
                  class="mr-2"
                  :color="stageIconColor(v, stage, si)"
                >
                  {{ stageIcon(v, stage, si) }}
                </v-icon>
                <div>
                  <div>{{ stage.name }}</div>
                  <div class="text-caption grey--text">
                    {{ (stage.members || []).map((m) => m.name).join(", ") }}
                  </div>
                </div>
                <div class="flex-grow-1" />
                <div
                  :class="stage.approvedAt ? 'success--text' : 'grey--text'"
                  class="text-caption text-right"
                >
                  <template v-if="stage.approvedAt">
                    {{ stage.approvedByName }}<br />
                    {{ dateUtil(new Date(stage.approvedAt), true, "-") }}
                  </template>
                  <template v-else-if="isActiveStage(v, si)">
                    {{ $t("riskAssessment.proof.awaiting") }}
                  </template>
                  <template v-else>
                    {{ $t("riskAssessment.proof.pending") }}
                  </template>
                </div>
              </div>

              <div
                v-if="!v.stages || !v.stages.length"
                class="grey--text text-caption"
              >
                {{ $t("riskAssessment.proof.noProofers") }}
              </div>
              <v-divider class="my-4" />
            </div>
          </div>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn text @click="versionsDialog = false">
            {{ $t("general.close") }}
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import { dateUtil } from "../../api/api";
export default {
  props: ["item"],
  data: () => ({
    versionsDialog: false,
    loadingVersions: false,
    versions: [],
    currentVersion: 0,
  }),
  computed: {
    currentVersionData() {
      const versions = (this.item && this.item.versions) || [];
      if (!versions.length) return null;
      return (
        versions.find((v) => v.version === this.item.currentVersion) ||
        versions[versions.length - 1]
      );
    },
    stages() {
      const version = this.currentVersionData;
      return (version && version.stages) || [];
    },
    status() {
      const version = this.currentVersionData;
      return (version && version.status) || "pending";
    },
    approvedCount() {
      return this.stages.filter((s) => s.approvedAt).length;
    },
    expired() {
      if (this.status !== "approved") return false;

      const times = this.stages
        .filter((s) => s.approvedAt)
        .map((s) => new Date(s.approvedAt).getTime());
      if (!times.length) return false;

      const oneYearAgo = new Date();
      oneYearAgo.setFullYear(oneYearAgo.getFullYear() - 1);
      return Math.max(...times) < oneYearAgo.getTime();
    },
    chipColor() {
      if (this.status === "approved" && !this.expired) return "success";
      return "error";
    },
    chipText() {
      if (this.status === "rejected") {
        return this.$t("riskAssessment.proof.rejected");
      }
      if (this.expired) {
        return this.$t("riskAssessment.proof.overdue");
      }
      if (this.status === "approved") {
        return this.$t("riskAssessment.proof.approved");
      }

      const version = this.currentVersionData;
      const active = this.stages[(version && version.currentStage) || 0];

      return this.$t("riskAssessment.proof.stageProgress", {
        done: this.approvedCount,
        total: this.stages.length,
        stage: active ? active.name : "",
      });
    },
    tooltipText() {
      const version = this.currentVersionData;
      if (this.status === "rejected" && version && version.rejection) {
        return version.rejection.reason;
      }
      return this.$t("riskAssessment.proof.viewHistory");
    },
  },
  methods: {
    dateUtil,
    isActiveStage(version, index) {
      return (
        version.status === "pending" && (version.currentStage || 0) === index
      );
    },
    stageIcon(version, stage, index) {
      if (stage.approvedAt) return "mdi-check-circle";
      if (this.isActiveStage(version, index)) return "mdi-clock-outline";
      return "mdi-circle-outline";
    },
    stageIconColor(version, stage, index) {
      if (stage.approvedAt) return "success";
      if (this.isActiveStage(version, index)) return "warning";
      return "grey";
    },
    async openVersions() {
      this.versionsDialog = true;
      this.loadingVersions = true;
      try {
        const result = await this.$store.dispatch(
          "getMedkitlogVersions",
          this.item._id || this.item.id
        );
        this.versions = (result && result.versions) || [];
        this.currentVersion = (result && result.currentVersion) || 0;
      } finally {
        this.loadingVersions = false;
      }
    },
  },
};
</script>

<style scoped>
.ra-proof-cell {
  padding: 4px 0;
}
.ra-proof-chip {
  cursor: pointer;
}
.ra-version-row {
  padding: 6px 0;
  font-size: 0.9rem;
}
.ra-rejection {
  padding: 10px 12px;
  background-color: #fdecea;
  border-left: 3px solid #f44336;
  border-radius: 2px;
}
.ra-version:last-child .v-divider {
  display: none;
}
</style>
